import {
  ChallengeSuccessPayload,
  GenericUpdateAction,
  UpdateAction,
  UpdateActionType,
  UpdateActionWithState,
} from "@/models/socketIo/updateAction";
import { Official, Person, Player, TeamSquad } from "@/models/gameGeneral";
import {
  ArrowDownward,
  ArrowUpward,
  AutoFixHigh,
  Block,
  Cancel,
  Check,
  CheckCircle,
  CloudSync,
  ExpandMore,
  Help,
  HideImage,
  HistoryEdu,
  Lock,
  MilitaryTech,
  Pause,
  PersonAdd,
  PersonOff,
  PlayArrow,
  Redo,
  Rocket,
  RocketLaunch,
  SportsVolleyballOutlined,
  SwapHoriz,
  SyncProblem,
  Timer,
  TimerOff,
  Tune,
  Undo,
  Videocam,
  WorkspacePremium,
} from "@mui/icons-material";
import {
  Box,
  Divider,
  Typography,
  TypographyProps,
  TypographyVariants,
} from "@mui/material";
import { ConfirmArbitration, EventHistoryScoreUpdate } from "@/models/ticker";
import { Challenger, PdfData, Score, TeamReference } from "@/models/pdfData";
import DelayYellowCard from "../custom-icons/DelayYellowCard";
import DelayRedCard from "../custom-icons/DelayRedCard";
import YellowCard from "../custom-icons/YellowCard";
import RedCard from "../custom-icons/RedCard";
import ExpulsionCard from "../custom-icons/ExpulsionCard";
import DisqualificationCard from "../custom-icons/DisqualificationCard";
import ExposurePlusOne from "../custom-icons/ExposurePlusOne";
import {
  millisToTime,
  millisToTimeRemaining,
} from "@/services/dateTimeService";
import LiberoSwitchIn from "../custom-icons/LiberoSwitchIn";
import LiberoSwitch from "../custom-icons/LiberoSwitch";
import LiberoSwitchOut from "../custom-icons/LiberoSwitchOut";
import LiberoIn from "../custom-icons/LiberoIn";
import LiberoOut from "../custom-icons/LiberoOut";
import { otherTeam } from "@/pdf/utils";
import DirectorySync from "../custom-icons/DirectorySync";
import Signature from "../custom-icons/Signature";
import SwapHorizAlert from "../custom-icons/SwapHorizAlert";
import Chronic from "../custom-icons/Chronic";
import { useState } from "react";
import { ChallengeTypeTranslations } from "../referee-tablet-page/RefereeTabletChallengeInfo";
import { gameState } from "@/services/gameState";
import { getAddressee } from "@/utils/persons";
import { Arbitration } from "@/models/socketIo/initializeMatchPayload";
import { formatScore } from "@context/utils";

const fallbackPlayer: Player = {
  lastName: "Unbekannt",
  jerseyNumber: "",
} as Player;

const nonVisibleActionTypes: UpdateActionType[] = [
  "[Liberos] Set default libero",
  "[Lineups] Cancel nomination",
  "[Lineups] Nominate",
  "[Lineups] Rotate anticlockwise",
  "[Lineups] Rotate clockwise",
  "[Match] Add Official",
  "[Match] Add Player",
  "[Match] Approved official license",
  "[Match] Approved player license",
  "[Match] Change Jersey Number",
  "[Match] Change jersey",
  "[Match] Denied official license",
  "[Match] Denied player license",
  "[Match] Edit Arbitration Member",
  "[Match] Edit Official",
  "[Match] Edit number of Spectators",
  "[Match] Set shirt color",
  "[Match] Set shorts color",
  "[Match] Toggle Referee",
  "[Match] Toggle libero registration",
  "[TeamHighlight] Highlight",
  "[TeamSquad] load",
  "[TeamSquad] reset all",
  "[TeamSquad] select all",
  "[TeamSquad] toggle captain",
  "[TeamSquad] toggle libero",
  "[TeamSquad] toggle official",
  "[TeamSquad] toggle player",
  "[TeamSquad] undo confirmation",
  "[Undoable] Redo previous action",
  "[Undoable] Undo last action",
];

export const isVisibleActionType = (type: UpdateActionType) =>
  !nonVisibleActionTypes.includes(type);

export const eventTextMap = (action: UpdateActionWithState): string => {
  switch (action.type) {
    case "[MatchState] Initialize Match":
      return "Initialisierung";
    case "[TeamSquad] confirm":
      return "Mannschaftsliste bestätigt";
    case "[Match] Confirm arbitration":
      return "Schiedsgericht bestätigt";
    case "[MatchSet] Lock starting six":
      return "Startaufstellung fixiert";
    case "[TimeKeeping] Start Match":
      return "Spielbeginn";
    case "[Set] Score":
      return `Punkt ${getTeamName(action)}`;
    case "[Lineups] Substitution":
      return `Wechsel ${getTeamName(action)}`;
    case "[Lineups] Exceptional Substitution":
      return `Ausnahmsweiser Wechsel ${getTeamName(action)}`;
    case "[Timeouts] Start timeout": {
      const teamCode = action.payload.teamCode;
      const shortName = getTeamName(action);
      return `Timeout ${shortName} ${action.timeoutsInSet[teamCode] ? `(${action.timeoutsInSet[teamCode]}. T/O)` : ""}`;
    }
    case "[Lineups] Libero in":
    case "[Lineups] Libero switch":
    case "[Lineups] Libero out":
      return `Liberotausch ${getTeamName(action)}`;
    case "[Timeouts] Cancel timeout":
      return "Timeout Abgebrochen";
    case "[Sanction] Team Improper Request":
      return "Unber. Antrag";
    case "[Sanction] Team Warning":
      return "Verw. Verzögerung";
    case "[Sanction] Team Penalty":
      return "Bstrf. Verzögerung";
    case "[Sanction] Individual Warning":
      return "Verwarnung";
    case "[Sanction] Individual Penalty":
      return "Bestrafung";
    case "[Sanction] Individual Expulsion":
      return "Hinausstellung";
    case "[Sanction] Individual Disqualification":
      return "Disqualifikation";
    case "[Set] Score override":
      return "Spielstand überschrieben";
    case "[Liberos] Libero unable to play":
      return "Spielunfähig";
    case "[Liberos] Add new libero":
      return "Liberoneubenennung";
    case "[TimeKeeping] Start":
      return "Satzbeginn";
    case "[Timeouts] Change set pause duration":
      return "Dauer Satzpause angepasst";
    case "[Mvps] Select mvp":
      return `MVP ${getTeamName(action)}`;
    case "[TeamSides] Switch Sides":
      return "Seitenwechsel";
    case "[Serving] Change Serving":
      return "Aufschlagwechsel";
    case "[Match] Confirm finalization team":
      return `Bestätigung ${getTeamName(action)}`;
    case "[Match] Confirm finalization referee":
      return "Bestätigung SR";
    case "[Match] Confirm finalization":
      return "Spielbericht finalisiert";
    case "[Match] Confirm finalization success":
      return "Spielbericht hochgeladen";
    case "[Match] Confirm finalization fail":
      return "Spielbericht NICHT hochgeladen";
    case "[Match] Edit start time":
      return "Startzeit angepasst";
    case "[Undoable] Undo last action":
      return "Aktion rückgängig";
    case "[Undoable] Redo previous action":
      return "Aktion wiederherstellen";
    case "[Match] Save Comments":
      return "Bemerkung geändert";
    case "[Challenges] Challenge":
      return `Challenge: ${getTeamName(action)}`;
    case "[Challenges] Challenge success":
      return getChallengeSuccessResult(action);
    case "[Challenges] Challenge Unresolved":
      return "Challenge nicht auflösbar";
    case "[Challenges] Challenge Declined":
      return "Challenge zurückgewiesen";
    case "[Challenges] Challenge unsuccessful":
      return "Challenge nicht erfolgreich";
    default:
      return action.type;
  }
};

const getTeamName = (
  action:
    | { payload: { teamCode: TeamReference } }
    | { payload: { challenger: Challenger } }
    | { payload: TeamReference },
): string => {
  const teamReference =
    typeof action.payload === "object"
      ? "challenger" in action.payload
        ? action.payload.challenger
        : action.payload.teamCode
      : action.payload;

  if (teamReference === "referee") {
    return "SR";
  } else {
    return gameState("teams")[teamReference].shortName;
  }
};

const getChallengeSuccessResult = (
  action: GenericUpdateAction<"[Challenges] Challenge success">,
): string => {
  if (action.payload.challenger === "referee") {
    if (action.payload.outcome === "DOUBLE_FAULT") {
      return "Doppelfehler";
    } else {
      return "Challenge aufgelöst";
    }
  } else {
    return "Challenge erfolgreich";
  }
};

export const eventIconMap = (action: UpdateActionWithState) => {
  switch (action.type) {
    case "[MatchState] Initialize Match":
      return <Rocket />;
    case "[TeamSquad] confirm":
    case "[Match] Confirm arbitration":
      return <Check />;
    case "[MatchSet] Lock starting six":
      return <Lock />;
    case "[TimeKeeping] Start Match":
      return <RocketLaunch />;
    case "[Mvps] Select mvp":
      return <WorkspacePremium />;
    case "[Set] Score":
      return <ExposurePlusOne />;
    case "[Lineups] Substitution":
      return <SwapHoriz />;
    case "[Lineups] Exceptional Substitution":
      return <SwapHorizAlert />;
    case "[Lineups] Libero in":
      return <LiberoSwitchIn />;
    case "[Lineups] Libero switch":
      return <LiberoSwitch />;
    case "[Lineups] Libero out":
      return <LiberoSwitchOut />;
    case "[Match] Confirm finalization":
      return <MilitaryTech />;
    case "[Timeouts] Start timeout":
      return <Timer />;
    case "[Timeouts] Cancel timeout":
      return <TimerOff />;
    case "[Sanction] Team Improper Request":
      return <Block />;
    case "[Sanction] Team Warning":
      return <DelayYellowCard />;
    case "[Sanction] Team Penalty":
      return <DelayRedCard />;
    case "[Sanction] Individual Warning":
      return <YellowCard />;
    case "[Sanction] Individual Penalty":
      return <RedCard />;
    case "[Sanction] Individual Expulsion":
      return <ExpulsionCard />;
    case "[Sanction] Individual Disqualification":
      return <DisqualificationCard />;
    case "[Set] Score override":
      return <AutoFixHigh />;
    case "[Liberos] Libero unable to play":
      return <PersonOff />;
    case "[Liberos] Add new libero":
      return <PersonAdd />;
    case "[TimeKeeping] Start":
      return <PlayArrow />;
    case "[TimeKeeping] End":
      return <Pause />;
    case "[Timeouts] Change set pause duration":
      return <Tune />;
    case "[TeamSides] Switch Sides":
      return <DirectorySync />;
    case "[Serving] Change Serving":
      return <SportsVolleyballOutlined />;
    case "[Match] Confirm finalization team":
    case "[Match] Confirm finalization referee":
      return <Signature />;
    case "[Match] Confirm finalization success":
      return <CloudSync />;
    case "[Match] Confirm finalization fail":
      return <SyncProblem />;
    case "[Match] Edit start time":
      return <Chronic />;
    case "[Undoable] Undo last action":
      return <Undo />;
    case "[Undoable] Redo previous action":
      return <Redo />;
    case "[Match] Save Comments":
      return <HistoryEdu />;
    case "[Challenges] Challenge":
      return <Videocam />;
    case "[Challenges] Challenge success":
      return <CheckCircle />;
    case "[Challenges] Challenge Unresolved":
      return <HideImage />;
    case "[Challenges] Challenge unsuccessful":
      return <Cancel />;
    case "[Challenges] Challenge Declined":
      return <Block />;
    default:
      return <Help />;
  }
};

export const eventSecondaryMap = ({
  action,
}: {
  action: UpdateActionWithState;
}) => {
  switch (action.type) {
    case "[Set] Score":
    case "[Timeouts] Cancel timeout":
    case "[MatchSet] Lock starting six":
    case "[TeamSides] Switch Sides":
    case "[Match] Confirm finalization team":
    case "[Match] Confirm finalization referee":
    case "[Match] Confirm finalization":
    case "[Undoable] Undo last action":
    case "[Undoable] Redo previous action":
    case "[Match] Confirm finalization fail":
      return null;
    case "[MatchState] Initialize Match":
      return getInitializeMatchSecondary(
        action as GenericUpdateAction<"[MatchState] Initialize Match">,
      );
    case "[TeamSquad] confirm":
      return getConfirmTeamSquad(
        action as GenericUpdateAction<"[TeamSquad] confirm">,
      );
    case "[Match] Confirm arbitration":
      return getConfirmArbitration();
    case "[TimeKeeping] Start Match":
      return getSecondary({
        geplant: `${millisToTime(action.payload.startTimeMs, true)} Uhr`,
        ist: `${millisToTime(action.payload.actualStartTimeMs, true)} Uhr`,
      });
    case "[Mvps] Select mvp":
      return getSelectMvpSecondary(action);
    case "[Timeouts] Start timeout":
      return getTimeOutSecondary();

    case "[Lineups] Substitution":
    case "[Lineups] Exceptional Substitution":
      return getSubstitutionSecondary(action);
    case "[Sanction] Individual Warning":
    case "[Sanction] Individual Penalty":
    case "[Sanction] Individual Expulsion":
    case "[Sanction] Individual Disqualification":
      return getIndividualSanctionSecondary(action);
    case "[Liberos] Libero unable to play":
      return getPersonFromAction(action);
    case "[Liberos] Add new libero":
      return getPersonFromAction(action);
    case "[TimeKeeping] Start":
      return getSecondary([
        `${action.setNumber}. Satz`,
        `${millisToTime(action.payload, true)} Uhr`,
      ]);
    case "[Lineups] Libero in":
      return getLiberoIn(action);
    case "[Lineups] Libero switch":
      return getLiberoSwitch(action);
    case "[Lineups] Libero out":
      return getLiberoOut(action);
    case "[Sanction] Team Improper Request":
    case "[Sanction] Team Warning":
    case "[Sanction] Team Penalty":
      return getTeamSanctionSecondary(action);
    case "[Timeouts] Change set pause duration":
      return getSecondary(
        `${millisToTimeRemaining(action.payload.newDuration)} Minuten`,
      );
    case "[Serving] Change Serving":
      const teams = gameState("teams");
      return getSecondary(`Aufschlag: ${teams[action.nextServe].shortName}`);
    case "[Match] Confirm finalization success":
      return getSecondary(`${millisToTime(action.timestamp, true)} Uhr`);
    case "[Match] Edit start time":
      return getSecondary(
        `Neue Startzeit: ${millisToTime(Date.parse(action.payload))} Uhr`,
      );
    case "[Match] Save Comments":
      const text = action.payload;
      if (text.length > 10) {
        return getExpandableSecondary(text);
      } else {
        return getSecondary(text);
      }
    case "[Challenges] Challenge":
      return getChallengeSecondary(action);
    case "[Challenges] Challenge Declined":
    case "[Challenges] Challenge unsuccessful":
    case "[Challenges] Challenge Unresolved":
    case "[Challenges] Challenge success":
      return getChallengeCompleteSecondary(action);
    case "[Set] Score override":
      return "Vorheriger Spielstand: " + formatScore(action.previousScore);
    default:
      return <Typography>Not implemented</Typography>;
  }
};

const getTimeOutSecondary = () => {
  const scoreConfiguration = gameState("scoreConfiguration");
  return getSecondary(`(${scoreConfiguration.timeoutDurationSeconds} s)`);
};

const getSelectMvpSecondary = (
  action: GenericUpdateAction<"[Mvps] Select mvp">,
) => {
  const teamSquads = gameState("teamSquads");
  const player: Player = getPlayerFromSquad(
    teamSquads[action.payload.teamCode],
    action.payload.playerUuid,
  );
  return getSecondary(`#${player.jerseyNumber}: ${player.lastName}`);
};

const getSubstitutionSecondary = (
  action: GenericUpdateAction<
    "[Lineups] Substitution" | "[Lineups] Exceptional Substitution"
  >,
) => {
  const teamSquads = gameState("teamSquads");
  const teamCode = action.payload.teamCode;
  const playerIn = getPlayerFromSquad(
    teamSquads[teamCode],
    action.payload.playerInId,
  );
  const playerOut = getPlayerFromSquad(
    teamSquads[teamCode],
    action.payload.playerOutId,
  );

  return (
    <Box display={"grid"} gridTemplateColumns={"auto 1fr"}>
      <ArrowUpward />
      {getPerson(playerIn, "body1")}
      <ArrowDownward fontSize={"small"} sx={{ justifySelf: "center" }} />
      {getPerson(playerOut, "body2")}
    </Box>
  );
};

const getTeamSanctionSecondary = (
  action: GenericUpdateAction<
    | "[Sanction] Team Warning"
    | "[Sanction] Team Penalty"
    | "[Sanction] Team Improper Request"
  >,
) => {
  const teams = gameState("teams");
  return getSecondary([
    teams[action.payload.teamCode].name,
    ...(action.type === "[Sanction] Team Penalty"
      ? [`+1 Punkt für ${teams[otherTeam(action.payload.teamCode)].shortName}`]
      : []),
  ]);
};

const getIndividualSanctionSecondary = (
  action: GenericUpdateAction<
    | "[Sanction] Individual Warning"
    | "[Sanction] Individual Penalty"
    | "[Sanction] Individual Expulsion"
    | "[Sanction] Individual Disqualification"
  >,
) => {
  const teamSquads = gameState("teamSquads");
  const teams = gameState("teams");

  const teamMember: Player | Official = getTeamMemberFromSquad(
    teamSquads[action.payload.teamCode],
    action.payload.teamMemberId,
  );

  const prefix =
    "function" in teamMember
      ? teamMember.function
      : `#${teamMember.jerseyNumber}`;

  return getSecondary([
    teams[action.payload.teamCode].name,
    `${prefix}: ${teamMember.firstName} ${teamMember.lastName}`,
  ]);
};

const getLiberoIn = (action: GenericUpdateAction<"[Lineups] Libero in">) => {
  const teamSquads = gameState("teamSquads");
  const { liberoId, liberoSubstitute } = action.payload;
  const libero = getPlayerFromSquad(
    teamSquads[action.payload.teamCode],
    liberoId,
  );
  const substitute = getPlayerFromSquad(
    teamSquads[action.payload.teamCode],
    liberoSubstitute,
  );

  return getLiberoExchange({ ...libero, isLibero: true }, substitute);
};

const getLiberoSwitch = (
  action: GenericUpdateAction<"[Lineups] Libero switch">,
) => {
  const teamSquads = gameState("teamSquads");

  const { liberoInId, liberoOutId } = action.payload;
  const liberoIn = getPlayerFromSquad(
    teamSquads[action.payload.teamCode],
    liberoInId,
  );
  const liberoOut = getPlayerFromSquad(
    teamSquads[action.payload.teamCode],
    liberoOutId,
  );

  return getLiberoExchange(
    { ...liberoIn, isLibero: true },
    { ...liberoOut, isLibero: true },
  );
};

const getLiberoOut = (action: GenericUpdateAction<"[Lineups] Libero out">) => {
  const teamSquads = gameState("teamSquads");
  const { liberoId, playerInId } = action.payload;
  const playerIn = getPlayerFromSquad(
    teamSquads[action.payload.teamCode],
    playerInId,
  );
  const libero = getPlayerFromSquad(
    teamSquads[action.payload.teamCode],
    liberoId,
  );

  return getLiberoExchange(playerIn, { ...libero, isLibero: true });
};

const getLiberoExchange = (playerIn: Player, playerOut: Player) => {
  return (
    <Box display={"grid"} gridTemplateColumns={"auto 1fr"}>
      <LiberoIn />
      {getPerson(playerIn, "body1")}
      <LiberoOut fontSize={"small"} sx={{ justifySelf: "center" }} />
      {getPerson(playerOut, "body2")}
    </Box>
  );
};

const getPersonFromAction = (
  action: GenericUpdateAction<
    "[Liberos] Libero unable to play" | "[Liberos] Add new libero"
  >,
) => {
  const teamSquads = gameState("teamSquads");

  const personId =
    "liberoId" in action.payload
      ? action.payload.liberoId
      : action.payload.playerId;
  const teamCode = action.payload.teamCode;

  const person = getTeamMemberFromSquad(teamSquads[teamCode], personId);

  return getPerson(person);
};

const getPerson = (
  person: Player | Official,
  variant: TypographyProps["variant"] = "body1",
) => {
  return (
    <Typography
      variant={variant}
      overflow={"hidden"}
      whiteSpace={"nowrap"}
      textOverflow={"ellipsis"}
    >
      {getAddressee(person)}
    </Typography>
  );
};

const getChallengeSecondary = (
  event: GenericUpdateAction<"[Challenges] Challenge">,
) => {
  const { challengeType } = event.payload;
  return getSecondary([
    ChallengeTypeTranslations[challengeType] || challengeType,
    ...(event.payload.challenger !== "referee"
      ? [`Verbleibende Challenges: ${event.payload.challengesLeft}`]
      : []),
  ]);
};

const getChallengeCompleteSecondary = (
  event:
    | GenericUpdateAction<"[Challenges] Challenge Unresolved">
    | GenericUpdateAction<"[Challenges] Challenge unsuccessful">
    | GenericUpdateAction<"[Challenges] Challenge success">
    | GenericUpdateAction<"[Challenges] Challenge Declined">,
) => {
  const { leftTeam, rightTeam } = gameState("teamSides");

  const beforeScore = event.payload.setScoreBeforeChallenge;
  let afterScore: Score = beforeScore;

  if ("outcome" in event.payload) {
    if (event.payload.challenger === "referee") {
      switch (event.payload.outcome) {
        case "DOUBLE_FAULT":
          afterScore = beforeScore;
          break;
        case "POINT_TEAM1":
          afterScore = { ...beforeScore, ["team1"]: beforeScore["team1"] + 1 };
          break;
        case "POINT_TEAM2":
          afterScore = { ...beforeScore, ["team2"]: beforeScore["team2"] + 1 };
          break;
      }
    } else {
      switch (event.payload.outcome) {
        case "DOUBLE_FAULT":
          afterScore = beforeScore;
          break;
        case "CONFIRMED":
          afterScore = {
            ...beforeScore,
            [event.payload.challenger]:
              beforeScore[event.payload.challenger] + 1,
          };
          break;
        case "POINT_REVERSED":
          afterScore = {
            [event.payload.challenger]:
              beforeScore[event.payload.challenger] + 1,
            [otherTeam(event.payload.challenger)]:
              beforeScore[otherTeam(event.payload.challenger)] - 1,
          } as Score;
      }
    }
  }

  const leftScoreTeam: TeamReference =
    event.payload.challenger === "referee"
      ? leftTeam
      : event.payload.challenger;
  const rightScoreTeam: TeamReference =
    event.payload.challenger === "referee"
      ? rightTeam
      : otherTeam(event.payload.challenger);

  const getScoreString = (score: Score) =>
    `${`${score[leftScoreTeam]}`.padStart(2, "0")} : ${`${score[rightScoreTeam]}`.padStart(2, "0")}`;
  return getSecondary([
    ...(afterScore ? [`${getScoreString(afterScore)} (Score danach)`] : []),
    `${getScoreString(beforeScore)} (Score vorher)`,
    `Zeitpunkt: ${event.payload.challenger === "referee" || event.payload.challengeMoment !== "DURING_RALLY" ? "Nach Spielzug" : "Während Spielzug"}`,
    ...(event.payload.challenger === "referee"
      ? []
      : [`Verbleibende Challenges: ${event.payload.challengesLeft}`]),
    `Dauer: ${Math.floor(event.payload.durationMs / 1000)}s`,
  ]);
};

const getInitializeMatchSecondary = (
  event: GenericUpdateAction<"[MatchState] Initialize Match">,
) => {
  const { matchConfiguration, matchSeries, scoreConfiguration, scoreVersion } =
    event.payload;

  const setInfo = matchConfiguration.alwaysPlayMaximumNumberOfSets
    ? `${matchConfiguration.maximumSets} Sätze`
    : `${matchConfiguration.setsToWin} Gewinnsätze`;

  const setPauseInfo =
    Object.values(scoreConfiguration.pauseDurationAfterSecondSet)
      .filter((v) => !!v)
      .map((seconds) => seconds / 60)
      .join(" / ") + " Min. Satzpause";

  const arbitration = renderArbitration(event.payload.arbitration);

  return (
    <Box>
      <Typography variant={"subtitle1"}>{matchSeries.name}</Typography>
      <Divider variant={"fullWidth"} sx={{ gridColumn: "1 / -1" }} />
      <Typography variant={"caption"}>Eingeteilte SRs:</Typography>
      {arbitration}
      <Typography variant={"caption"}>Spiel Einstellungen:</Typography>
      <br />
      {getSecondary([
        setInfo,
        setPauseInfo,
        `SAMS Version: ${scoreVersion}`,
        ...(scoreConfiguration.videoChallengeAvailable
          ? [`Challenge verfügbar`]
          : [`Keine Challenge verfügbar`]),
      ])}
    </Box>
  );
};

const getConfirmTeamSquad = (
  event: GenericUpdateAction<"[TeamSquad] confirm">,
) => {
  const teams = gameState("teams");
  const gender = gameState("gender");
  const teamSquads = gameState("teamSquads");

  return getSecondary({
    [`${teams[event.payload.teamCode].name}`]: "",
    [gender === "MALE" ? "Spieler" : "Spielerinnen"]:
      teamSquads[event.payload.teamCode].players.length,
    ["Offizielle"]: teamSquads[event.payload.teamCode].officials.length,
    [gender === "MALE" ? "Liberos" : "Liberas"]:
      teamSquads[event.payload.teamCode].liberos.length,
    [gender === "MALE" ? "Kapitän" : "Kapitänin"]:
      `#${teamSquads[event.payload.teamCode].captain?.jerseyNumber}`,
  } as Record<string, string>);
};

const getConfirmArbitration = () => {
  const arbitration = gameState("arbitration");

  return renderArbitration(arbitration);
};

const renderArbitration = (arbitration: Arbitration) => {
  const buildName = (person?: Person) =>
    person ? `${person.firstName} ${person.lastName}` : "";

  return getSecondary(
    {
      "1. SR": buildName(arbitration.firstReferee),
      "2. SR": buildName(arbitration.secondReferee),
      ...(arbitration.challengeReferee
        ? { VAR: buildName(arbitration.challengeReferee) }
        : {}),
      ...(arbitration.firstLinesman
        ? { "1. LR": buildName(arbitration.firstLinesman) }
        : {}),
      ...(arbitration.secondLinesman
        ? { "2. LR": buildName(arbitration.secondLinesman) }
        : {}),
      ...(arbitration.thirdLinesman
        ? { "3. LR": buildName(arbitration.thirdLinesman) }
        : {}),
      ...(arbitration.fourthLinesman
        ? { "4. LR": buildName(arbitration.fourthLinesman) }
        : {}),
      ...(arbitration.supervisor
        ? { SV: buildName(arbitration.supervisor) }
        : {}),
      ...(arbitration.observer
        ? { "Beob.": buildName(arbitration.observer) }
        : {}),
      ...(arbitration.scorer ? { Scorer: buildName(arbitration.scorer) } : {}),
      ...(arbitration.scorerAssistant
        ? { "Score Assist": buildName(arbitration.scorerAssistant) }
        : {}),
    },
    true,
  );
};

export const getPlayerFromSquad = (
  teamSquad: TeamSquad,
  playerUuid: string,
): Player =>
  teamSquad.players.find((p) => p.uuid === playerUuid) || fallbackPlayer;

export const getTeamMemberFromSquad = (
  teamSquad: TeamSquad,
  memberUuid: string,
): Player | Official =>
  getPlayerFromSquad(teamSquad, memberUuid) ||
  teamSquad.officials.find((o) => o.uuid === memberUuid) ||
  fallbackPlayer;

const getSecondaryForScoreUpdate = (
  game: PdfData,
  event: EventHistoryScoreUpdate,
  text?: string | string[],
) => {
  const teamA = event.teamCode;
  const teamB = event.teamCode === "team1" ? "team2" : "team1";

  return getSecondary([
    `${game.gameData[teamA].shortName} (${event.setScore[teamA]}:${event.setScore[teamB]})`,
    ...(text ? (Array.isArray(text) ? text : [text]) : []),
  ]);
};
const getSecondary = (
  text?: string | string[] | Record<string, string>,
  showDivider?: boolean,
) => {
  const renderText = (text: string, i: string = "0") => (
    <span key={i}>
      {text}
      {<br />}
    </span>
  );

  if (text && Array.isArray(text)) {
    return (
      <Typography component="span" variant="body2">
        {text.map((t, i) => renderText(t, i.toString()))}
      </Typography>
    );
  } else if (text && typeof text === "string") {
    return (
      <Typography
        component="span"
        variant="body2"
        sx={{ textOverflow: "inherit" }}
      >
        {renderText(text)}
      </Typography>
    );
  } else if (text && typeof text === "object") {
    return (
      <Box
        display={"grid"}
        gridTemplateColumns={"auto 1fr"}
        columnGap={"1vw"}
        width={"100%"}
      >
        {Object.entries(text).map(([key, value]) => (
          <>
            <Box
              sx={{
                display: "inline-grid",
                gridColumnStart: 1,
                gridColumnEnd: !!value ? 2 : 3,
              }}
            >
              <Typography
                variant="body2"
                textAlign={!!value ? "right" : "center"}
                alignSelf={"center"}
              >
                {key}
              </Typography>
            </Box>
            {!!value && (
              <Box
                sx={{
                  display: "inline-grid",
                  gridColumnStart: 2,
                }}
              >
                <Typography variant="body2">{value}</Typography>
              </Box>
            )}
            {showDivider && (
              <Divider variant={"fullWidth"} sx={{ gridColumn: "1 / -1" }} />
            )}
          </>
        ))}
      </Box>
    );
  } else {
    return null;
  }
};

const getExpandableSecondary = (
  text?: string | string[] | Record<string, string>,
) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const expandedBoxSx = {};
  const retractedBoxSx = {
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
  };

  return (
    <Box
      sx={{
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        gap: "1vw",
      }}
      onClick={(e) => {
        e.stopPropagation();
        setIsExpanded(!isExpanded);
      }}
    >
      <Box sx={isExpanded ? expandedBoxSx : retractedBoxSx}>
        {getSecondary(text)}
      </Box>
      <ExpandMore
        sx={{
          transform: `rotateX(${isExpanded ? 180 : 0}deg)`,
          transition: "transform 0.2s",
        }}
      />
    </Box>
  );
};
